import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronLeft, ChevronUp } from 'lucide-react';

import { ApiError, api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import type { AdminUserSummary } from './types';

interface AuditEntry {
  id: string;
  createdAt: string;
  action: string;
  entityType: string | null;
  entityId: string | null;
  actorIp: string | null;
  actorUserAgent: string | null;
  actor: { id: string; fullName: string; email: string } | null;
  beforeJson: unknown;
  afterJson: unknown;
}

interface AuditPage {
  items: AuditEntry[];
  total: number;
  limit: number;
  offset: number;
  from: string;
  to: string;
}

const PAGE_SIZE = 50;

function defaultRange(): { from: string; to: string } {
  const today = new Date();
  const from = new Date(today);
  from.setDate(from.getDate() - 29);
  return { from: ymd(from), to: ymd(today) };
}

function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function AdminAudit() {
  const [{ from, to }, setRange] = useState(defaultRange);
  const [actorUserId, setActorUserId] = useState<string>('');
  const [action, setAction] = useState<string>('');
  const [entityType, setEntityType] = useState<string>('');
  const [offset, setOffset] = useState(0);
  const [accumulated, setAccumulated] = useState<AuditEntry[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);

  const filtersKey = useMemo(
    () => ({
      from,
      to,
      actorUserId: actorUserId || null,
      action: action || null,
      entityType: entityType || null,
    }),
    [from, to, actorUserId, action, entityType],
  );

  const usersQuery = useQuery({
    queryKey: ['admin', 'users', { includeInactive: true }],
    queryFn: () => api<AdminUserSummary[]>('/admin/users?includeInactive=true'),
  });

  const actionsQuery = useQuery({
    queryKey: ['admin', 'audit', 'actions'],
    queryFn: () => api<string[]>('/admin/audit/actions'),
  });

  const entityTypesQuery = useQuery({
    queryKey: ['admin', 'audit', 'entity-types'],
    queryFn: () => api<string[]>('/admin/audit/entity-types'),
  });

  const pageQuery = useQuery({
    queryKey: ['admin', 'audit', 'list', filtersKey, offset],
    queryFn: () => {
      const params = new URLSearchParams({
        from,
        to,
        limit: String(PAGE_SIZE),
        offset: String(offset),
      });
      if (actorUserId) params.set('actorUserId', actorUserId);
      if (action) params.set('action', action);
      if (entityType) params.set('entityType', entityType);
      return api<AuditPage>(`/admin/audit?${params.toString()}`);
    },
  });

  useEffect(() => {
    if (!pageQuery.data) return;
    setAccumulated((prev) => {
      if (offset === 0) return pageQuery.data!.items;
      const seen = new Set(prev.map((p) => p.id));
      const novel = pageQuery.data!.items.filter((it) => !seen.has(it.id));
      return [...prev, ...novel];
    });
  }, [pageQuery.data, offset]);

  function applyFilters() {
    setOffset(0);
    setOpenId(null);
  }

  function loadMore() {
    setOffset(accumulated.length);
  }

  const total = pageQuery.data?.total ?? 0;
  const hasMore = accumulated.length < total;

  const selectCls =
    'h-9 w-full rounded-sm border border-input bg-card px-3 text-base text-foreground focus-visible:border-icaria-600 focus-visible:outline-none transition-colors disabled:opacity-50';

  return (
    <div className="p-6 md:p-8">
      <div className="mb-3">
        <Link
          to="/admin"
          className="inline-flex items-center gap-1.5 text-[12px] font-medium text-muted-foreground hover:text-icaria-600 transition-colors"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
          Volver a Admin
        </Link>
      </div>

      <div className="bg-card border border-border rounded-sm shadow-md overflow-hidden">
        <header className="px-7 py-5 border-b border-border">
          <h2 className="text-lg font-semibold tracking-tightish">Auditoría</h2>
          <p className="text-[13px] text-muted-foreground mt-1">
            Registro de acciones sensibles del sistema. Por defecto últimos 30 días.
          </p>
        </header>

        <div className="px-7 py-4 border-b border-border bg-paper/40">
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="aud-from">Desde</Label>
              <Input
                id="aud-from"
                type="date"
                value={from}
                onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="aud-to">Hasta</Label>
              <Input
                id="aud-to"
                type="date"
                value={to}
                onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="aud-actor">Actor</Label>
              <select
                id="aud-actor"
                value={actorUserId}
                onChange={(e) => setActorUserId(e.target.value)}
                disabled={usersQuery.isLoading}
                className={selectCls}
              >
                <option value="">Cualquiera</option>
                {(usersQuery.data ?? []).map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.fullName}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="aud-action">Acción</Label>
              <select
                id="aud-action"
                value={action}
                onChange={(e) => setAction(e.target.value)}
                disabled={actionsQuery.isLoading}
                className={selectCls}
              >
                <option value="">Todas</option>
                {(actionsQuery.data ?? []).map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="aud-entity">Entidad</Label>
              <select
                id="aud-entity"
                value={entityType}
                onChange={(e) => setEntityType(e.target.value)}
                disabled={entityTypesQuery.isLoading}
                className={selectCls}
              >
                <option value="">Todas</option>
                {(entityTypesQuery.data ?? []).map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-3 mt-4 flex-wrap">
            <Button type="button" variant="primary" size="sm" onClick={applyFilters} disabled={pageQuery.isFetching}>
              Aplicar filtros
            </Button>
            <span
              className="font-mono text-[10px] font-medium uppercase text-muted-foreground"
              style={{ letterSpacing: '0.14em' }}
            >
              {pageQuery.data ? `${total} resultados · mostrando ${accumulated.length}` : ''}
            </span>
          </div>
        </div>

        {pageQuery.isLoading && offset === 0 ? (
          <p className="px-7 py-10 text-center text-sm text-muted-foreground">Cargando…</p>
        ) : pageQuery.error ? (
          <p role="alert" className="px-7 py-10 text-center text-sm text-destructive">
            {pageQuery.error instanceof ApiError ? pageQuery.error.message : 'Error cargando auditoría'}
          </p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full" style={{ minWidth: 720 }}>
                <thead>
                  <tr>
                    <Th>Cuándo</Th>
                    <Th>Actor</Th>
                    <Th>Acción</Th>
                    <Th>Entidad</Th>
                    <Th align="right">{''}</Th>
                  </tr>
                </thead>
                <tbody>
                  {accumulated.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-5 py-10 text-center text-sm text-muted-foreground">
                        Sin resultados.
                      </td>
                    </tr>
                  ) : (
                    accumulated.map((e) => (
                      <AuditRow
                        key={e.id}
                        entry={e}
                        open={openId === e.id}
                        onToggle={() => setOpenId((curr) => (curr === e.id ? null : e.id))}
                      />
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {hasMore ? (
              <div className="px-7 py-4 border-t border-border flex justify-center">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={loadMore}
                  disabled={pageQuery.isFetching}
                >
                  {pageQuery.isFetching ? 'Cargando…' : 'Cargar más'}
                </Button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

function Th({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
}) {
  return (
    <th
      className={cn(
        'font-mono text-[12px] font-medium uppercase text-muted-foreground bg-paper border-b border-border px-5 py-3.5',
        align === 'right' ? 'text-right' : 'text-left',
      )}
      style={{ letterSpacing: '0.08em' }}
    >
      {children}
    </th>
  );
}

function AuditRow({
  entry,
  open,
  onToggle,
}: {
  entry: AuditEntry;
  open: boolean;
  onToggle: () => void;
}) {
  const before = entry.beforeJson;
  const after = entry.afterJson;
  const hasDetails = Boolean(
    (before && typeof before === 'object' && Object.keys(before as object).length > 0) ||
      (after && typeof after === 'object' && Object.keys(after as object).length > 0),
  );
  return (
    <>
      <tr
        className={cn(
          'border-b border-border',
          hasDetails && 'cursor-pointer hover:bg-paper transition-colors',
        )}
        onClick={hasDetails ? onToggle : undefined}
      >
        <td className="px-5 py-3 align-top">
          <div className="font-mono text-[12px] tabular-nums">{formatDate(entry.createdAt)}</div>
          <div className="font-mono text-[11px] text-muted-foreground tabular-nums">
            {formatTime(entry.createdAt)}
          </div>
        </td>
        <td className="px-5 py-3 align-top">
          {entry.actor ? (
            <>
              <div className="text-[13px] font-medium">{entry.actor.fullName}</div>
              <div className="font-mono text-[11px] text-muted-foreground truncate max-w-[200px]">
                {entry.actor.email}
              </div>
            </>
          ) : (
            <span className="text-[12px] text-muted-foreground">— sistema/anónimo</span>
          )}
          {entry.actorIp ? (
            <div className="font-mono text-[10px] text-muted-foreground mt-0.5">{entry.actorIp}</div>
          ) : null}
        </td>
        <td className="px-5 py-3 align-top">
          <Badge tone={actionTone(entry.action)}>{entry.action}</Badge>
        </td>
        <td className="px-5 py-3 align-top">
          {entry.entityType ? (
            <>
              <div className="text-[13px]">{entry.entityType}</div>
              {entry.entityId ? (
                <div className="font-mono text-[10px] text-muted-foreground truncate max-w-[200px]">
                  {entry.entityId}
                </div>
              ) : null}
            </>
          ) : (
            <span className="text-[12px] text-muted-foreground">—</span>
          )}
        </td>
        <td className="px-5 py-3 align-top text-right text-muted-foreground w-10">
          {hasDetails ? (
            open ? <ChevronUp className="h-4 w-4 inline" /> : <ChevronDown className="h-4 w-4 inline" />
          ) : null}
        </td>
      </tr>
      {open && hasDetails ? (
        <tr className="border-b border-border bg-paper/60">
          <td colSpan={5} className="px-5 py-4">
            <div className="grid sm:grid-cols-2 gap-3 text-[12px]">
              <div>
                <p
                  className="font-mono text-[10px] font-medium uppercase text-muted-foreground mb-1.5"
                  style={{ letterSpacing: '0.14em' }}
                >
                  Antes
                </p>
                <pre className="bg-card border border-border rounded-sm p-3 overflow-auto whitespace-pre-wrap font-mono text-[11px] max-h-64">
                  {before ? JSON.stringify(before, null, 2) : '—'}
                </pre>
              </div>
              <div>
                <p
                  className="font-mono text-[10px] font-medium uppercase text-muted-foreground mb-1.5"
                  style={{ letterSpacing: '0.14em' }}
                >
                  Después
                </p>
                <pre className="bg-card border border-border rounded-sm p-3 overflow-auto whitespace-pre-wrap font-mono text-[11px] max-h-64">
                  {after ? JSON.stringify(after, null, 2) : '—'}
                </pre>
              </div>
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}

function actionTone(action: string): 'green' | 'amber' | 'red' | 'blue' | 'neutral' {
  if (action.includes('approve')) return 'green';
  if (action.includes('reject') || action.includes('delete')) return 'red';
  if (action.includes('edit') || action.includes('correction')) return 'amber';
  if (action.includes('create')) return 'blue';
  return 'neutral';
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}
